import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React from "react";
import { TranslationsStore } from "../../store/TranslationsStore";
import { BorderStyle } from "@pulse/shared-components";
import {
  CornerRadius,
  FoundationColorTokens,
  Typography as LeoTypography,
  Spacing,
  Tabs,
  useRadioButtonColorTokens,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { TranslationDataList } from "./TranslationDataList";
import {
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import { getRadioButtonStyleProps } from "@pulse/shared-components";
import {
  processSurveyParams,
  NavigateToFunctions,
} from "../../../../routes/RoutesHelper";

export interface TranslationDataTabsProps {
  translationsStore: Instance<typeof TranslationsStore>;
  border: BorderStyle;
  borderRadius: CornerRadius;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  t: TFunction;
  spacing: Spacing;
  navigateTo: NavigateToFunctions;
}

export const TranslationDataTabs = observer(
  ({
    translationsStore,
    border,
    borderRadius,
    typography,
    tokens,
    t,
    spacing,
    navigateTo,
  }: TranslationDataTabsProps): React.ReactElement => {
    const radioButtonTokens = useRadioButtonColorTokens();
    const radioButtonStyleProps = getRadioButtonStyleProps(
      tokens,
      radioButtonTokens,
      spacing,
    );

    const getRTLSettings = (): React.ReactElement => {
      const currentLanguage = translationsStore.translatedLanguage;
      if (!currentLanguage) {
        return <></>;
      }
      const currentDirectionValue = currentLanguage.isRTL ? "RTL" : "LTR";

      return (
        <Stack direction="row" alignItems="center" gap={spacing.spaceLG}>
          <Typography sx={{ ...typography.sh3 }}>
            {t("surveys.translations.languageConfiguration.label")}:
          </Typography>
          {translationsStore.isUpdatingLanguageConfiguration && (
            <CircularProgress size={16} sx={{ marginLeft: spacing.spaceXS }} />
          )}
          <RadioGroup
            value={currentDirectionValue}
            onChange={(e): void => {
              const isRTL = e.target.value === "RTL";
              processSurveyParams(async (surveyId, projectId) => {
                await translationsStore.updateLanguageConfiguration(
                  surveyId,
                  projectId,
                  currentLanguage.languageId,
                  isRTL,
                );
                await translationsStore.getAllTranslations(surveyId, projectId);
              }, navigateTo);
            }}
            sx={{ display: "flex", flexDirection: "row", gap: spacing.spaceMD }}
          >
            <FormControlLabel
              value="LTR"
              label={t(
                "surveys.translations.languageConfiguration.leftToRight",
              )}
              control={<Radio {...radioButtonStyleProps} />}
              checked={currentDirectionValue === "LTR"}
              disabled={translationsStore.isUpdatingLanguageConfiguration}
            />
            <FormControlLabel
              value="RTL"
              label={t(
                "surveys.translations.languageConfiguration.rightToLeft",
              )}
              control={<Radio {...radioButtonStyleProps} />}
              checked={currentDirectionValue === "RTL"}
              disabled={translationsStore.isUpdatingLanguageConfiguration}
            />
          </RadioGroup>
        </Stack>
      );
    };

    const TranslationTabOrList = observer((): React.ReactElement => {
      if (translationsStore.isSingleLanguagePresent) {
        return (
          <Stack gap={spacing.spaceXL}>
            {getRTLSettings()}
            <TranslationDataList
              translationsStore={translationsStore}
              border={border}
              borderRadius={borderRadius}
              typography={typography}
              tokens={tokens}
              t={t}
              spacing={spacing}
            />
          </Stack>
        );
      } else {
        return (
          <Stack gap={spacing.spaceXL}>
            <Tabs
              tabs={translationsStore.translationListTabs}
              onTabChange={(index: number) => {
                translationsStore.setSelectedLanguageBasedOnIndex(index);
              }}
              selectedTabIndex={
                translationsStore.selectedLanguage?.tabIndex ?? 0
              }
            />
            {getRTLSettings()}
            <TranslationDataList
              translationsStore={translationsStore}
              border={border}
              borderRadius={borderRadius}
              typography={typography}
              tokens={tokens}
              t={t}
              spacing={spacing}
            />
          </Stack>
        );
      }
    });

    return (
      <Stack margin={`${spacing.spaceXL} 0px`} gap={spacing.spaceLG}>
        <TranslationTabOrList />
      </Stack>
    );
  },
);
